import React, { useState, useEffect } from 'react';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faPencilAlt,
    faTrash,
    faPuzzlePiece,
    faUserGraduate,
    faFileAlt,
    faDatabase,
    faQuestionCircle,
    faBook
} from '@fortawesome/free-solid-svg-icons';
import DeleteAlert from '../Shared/DeleteAlert';
import { useNavigate } from 'react-router-dom';

const AdminSubscriptionAddon = () => {
    const navigate = useNavigate();
    const [addons, setAddons] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters & Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // Dialogs
    const [deleteDialog, setDeleteDialog] = useState({ open: false, addon: null });

    const fetchAddons = async (page = pageNumber, search = searchTerm) => {
        setLoading(true);
        try {
            const params = {
                PageSize: pageSize,
                PageNumber: page,
                IsActive: true
            };

            if (search) params.Word = search;

            const response = await ApiServices.get('Subscription/User/Addons/getAll', params);
            if (response && response.check && response.data) {
                setAddons(response.data.items || []);
                setTotalPages(response.data.total_pages || 1);
                setTotalItems(response.data.total_records || 0);
            }
        } catch (error) {
            toastHelper.error('فشل في جلب الإضافات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAddons();
    }, [pageNumber, pageSize]);

    const handleSearch = (word) => {
        setSearchTerm(word);
        setPageNumber(1);
        fetchAddons(1, word);
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            const response = await ApiServices.delete(`Subscription/User/Addons/delete/${deleteDialog.addon.id}`);
            if (response && response.check) {
                toastHelper.success('تم حذف الإضافة بنجاح');
                fetchAddons();
            } else {
                toastHelper.error(response.message || 'فشل في حذف الإضافة');
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء الحذف');
        } finally {
            setLoading(false);
            setDeleteDialog({ open: false, addon: null });
        }
    };

    const getAddonTypeInfo = (type) => {
        switch (type) {
            case 1: return { name: 'حد الطلاب', icon: faUserGraduate, color: '#6366f1' };
            case 2: return { name: 'حد الامتحانات', icon: faFileAlt, color: '#10b981' };
            case 3: return { name: 'حد المساحة', icon: faDatabase, color: '#6366f1' };
            case 4: return { name: 'حد الأسئلة', icon: faQuestionCircle, color: '#ef4444' };
            case 5: return { name: 'حد المواد', icon: faBook, color: '#8b5cf6' };
            default: return { name: `نوع ${type}`, icon: faPuzzlePiece, color: '#64748b' };
        }
    };

    const columns = [
        { key: 'user_name', label: 'المعلم', sortable: true },
        {
            key: 'type',
            label: 'نوع الإضافة',
            render: (val) => {
                const info = getAddonTypeInfo(val);
                return (
                    <div className="addon-type-badge" style={{ color: info.color }}>
                        <FontAwesomeIcon icon={info.icon} />
                        <span>{info.name}</span>
                    </div>
                );
            }
        },
        {
            key: 'extra_value',
            label: 'القيمة المضافة',
            render: (val) => <span className="value-badge">+{val}</span>
        },
        {
            key: 'created_at',
            label: 'تاريخ الإضافة',
            render: (val) => val ? new Date(val).toLocaleDateString('ar-EG') : '-'
        }
    ];

    const actions = (row) => [
        {
            label: 'تعديل',
            icon: <FontAwesomeIcon icon={faPencilAlt} />,
            onClick: (row) => navigate('/admin/subscription-addons/edit', { state: { addon: row } }),
            className: 'edit-btn'
        },
        {
            label: 'حذف',
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: (row) => setDeleteDialog({ open: true, addon: row }),
            className: 'delete-btn'
        }
    ];

    return (
        <div className="admin-addons-container">
            <div className="page-header">
                <div className="header-info">
                    <h2>إضافات الاشتراكات</h2>
                    <p>إدارة الميزات الإضافية والحدود الممنوحة للمعلمين ({totalItems})</p>
                </div>
                <button className="add-addon-btn" onClick={() => navigate('/admin/subscription-addons/add')}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>إضافة ميزة</span>
                </button>
            </div>

            <SoftGrid
                columns={columns}
                data={addons}
                isLoading={loading}
                isPaginated={true}
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalPages={totalPages}
                onPageChange={setPageNumber}
                onPageSizeChange={setPageSize}
                onSearch={handleSearch}
                searchPlaceholder="ابحث باسم المعلم..."
                actions={actions}
            />

            <DeleteAlert
                isOpen={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, addon: null })}
                onConfirm={handleDelete}
                itemName={`${getAddonTypeInfo(deleteDialog.addon?.type).name} للمعلم ${deleteDialog.addon?.user_name}`}
                loading={loading}
            />

            <style>{`
                .admin-addons-container {
                    padding: 20px;
                    direction: rtl;
                }
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .header-info h2 {
                    margin: 0;
                    color: #1e293b;
                    font-size: 1.5rem;
                    font-weight: 800;
                }
                .header-info p {
                    margin: 4px 0 0;
                    color: #64748b;
                }
                .add-addon-btn {
                    background: #6366f1;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.4);
                }
                .add-addon-btn:hover {
                    background: #4f46e5;
                    transform: translateY(-1px);
                }
                
                .addon-type-badge {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 700;
                    font-size: 0.9rem;
                    background: #f8fafc;
                    padding: 6px 12px;
                    border-radius: 8px;
                    width: fit-content;
                }
                .value-badge {
                    background: #e0f2fe;
                    color: #0369a1;
                    padding: 4px 10px;
                    border-radius: 6px;
                    font-weight: 800;
                    font-size: 0.9rem;
                }
            `}</style>
        </div>
    );
};

export default AdminSubscriptionAddon;
